import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Inbox, MessageCircle, Phone, Webhook, Sparkles, CheckCheck, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const STATUS_STYLE = {
  pending: "border-sg-line text-sg-mute",
  sent: "border-emerald-400 text-emerald-700 bg-emerald-50",
  disabled: "border-sg-line text-sg-mute",
};

export default function LeadReceipts() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState({ receipts: [], unseen_count: 0 });
  const [loading, setLoading] = useState(true);
  const [savingPlan, setSavingPlan] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/lead-receipts", { params: { limit: 100 } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setWebhookUrl(user?.webhook_url || "");
  }, [user?.webhook_url]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const togglePro = async (val) => {
    setSavingPlan(true);
    try {
      const { data } = await api.post("/auth/pro-plan", {
        pro_plan: val,
        webhook_url: webhookUrl || null,
      });
      setUser(data);
      toast.success(val ? "Pro Plan enabled. Lead Receipts are live." : "Pro Plan disabled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update plan.");
    } finally {
      setSavingPlan(false);
    }
  };

  const saveWebhook = async () => {
    setSavingPlan(true);
    try {
      const { data } = await api.post("/auth/pro-plan", {
        pro_plan: user?.pro_plan ?? false,
        webhook_url: webhookUrl || null,
      });
      setUser(data);
      toast.success("Webhook URL saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save webhook.");
    } finally {
      setSavingPlan(false);
    }
  };

  const testWebhook = async () => {
    try {
      await api.post("/lead-receipts/test-webhook");
      toast.success("Test webhook fired. A sample receipt will appear in your stream.");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed.");
    }
  };

  const markSeen = async (id) => {
    try {
      await api.post(`/lead-receipts/${id}/seen`);
      load();
    } catch {
      toast.error("Could not mark seen.");
    }
  };

  const markAllSeen = async () => {
    try {
      await api.post("/lead-receipts/mark-all-seen");
      toast.success("All receipts marked as seen.");
      load();
    } catch {
      toast.error("Could not mark all.");
    }
  };

  return (
    <DashboardShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline text-sg-gold-deep flex items-center gap-2">
            PRO PLAN · LEAD RECEIPTS
            {user?.pro_plan ? (
              <span className="chip chip-gold" data-testid="pro-plan-badge">PRO</span>
            ) : (
              <span className="chip" data-testid="free-plan-badge">FREE</span>
            )}
          </div>
          <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
            Every WhatsApp tap, <span className="font-serif-italic text-sg-gold-deep">receipted.</span>
          </h1>
          <p className="text-sg-mute mt-2 max-w-2xl">
            Real-time stream of high-intent taps on your own branded portal — with optional webhook delivery to your CRM.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.unseen_count > 0 && (
            <Button variant="outline" onClick={markAllSeen} className="rounded-full border-sg-line" data-testid="mark-all-seen">
              <CheckCheck className="w-4 h-4 mr-1" /> Mark all seen
            </Button>
          )}
        </div>
      </div>

      {/* Pro Plan settings */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="sg-card-dark lg:col-span-2" data-testid="pro-plan-card">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="overline text-sg-gold">PRO PLAN</div>
              <h3 className="font-display text-2xl mt-1">
                Receipts + CRM <span className="font-serif-italic text-sg-gold">Webhook.</span>
              </h3>
              <p className="text-sm text-white/75 mt-2 max-w-lg">
                Every WhatsApp & Call tap on your own branded portal fires a JSON webhook to your CRM with project, locality, device, and source.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Label className="text-white/75 text-sm hidden md:inline">Pro Plan</Label>
              <Switch
                checked={!!user?.pro_plan}
                onCheckedChange={togglePro}
                disabled={savingPlan}
                data-testid="pro-plan-toggle"
              />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label className="overline text-sg-gold">WEBHOOK URL</Label>
              <Input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://your-crm.example.com/inpropportal/lead"
                className="mt-1 rounded-full bg-white/10 border-white/20 text-white font-mono placeholder:text-white/40"
                data-testid="webhook-url-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveWebhook} disabled={savingPlan} className="rounded-full bg-sg-gold hover:bg-sg-gold-soft text-sg-navy" data-testid="save-webhook">
                Save
              </Button>
              <Button onClick={testWebhook} disabled={savingPlan || !user?.webhook_url} className="rounded-full bg-white text-sg-navy hover:bg-sg-cream-soft" data-testid="test-webhook">
                <Webhook className="w-4 h-4 mr-1" /> Test
              </Button>
            </div>
          </div>
          <p className="mt-3 text-xs text-white/55">
            POST application/json · sample payload: <span className="font-mono">{"{event, agent_slug, project_name, locality_tag, device, occurred_at, receipt_id, utm}"}</span>
          </p>
        </div>

        <div className="sg-card-cream" data-testid="receipts-kpi-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white border border-sg-line flex items-center justify-center"><Inbox className="w-4 h-4 text-sg-gold-deep" /></div>
            <div>
              <div className="overline text-sg-gold-deep">UNSEEN</div>
              <div className="rupee text-4xl text-sg-navy mt-1" data-testid="unseen-count">{data.unseen_count}</div>
            </div>
          </div>
          <p className="text-sm text-sg-mute mt-4">
            High-intent taps you haven't followed up on yet.
          </p>
          <Link to="/dashboard/analytics" className="pill-outline inline-flex items-center gap-2 mt-5 border-sg-gold/50 text-sg-gold-deep">
            <Sparkles className="w-4 h-4" /> View Analytics
          </Link>
        </div>
      </div>

      {/* Stream */}
      <div className="mt-10 bg-white border border-sg-line rounded-3xl overflow-hidden" data-testid="receipts-stream">
        <div className="px-6 py-4 border-b border-sg-line flex items-center justify-between">
          <div className="overline text-sg-gold-deep">RECEIPTS STREAM</div>
          <span className="text-xs text-sg-mute font-mono">{loading ? "syncing…" : `auto-refresh · every 20s`}</span>
        </div>
        {data.receipts.length === 0 && !loading ? (
          <div className="px-6 py-16 text-center" data-testid="receipts-empty">
            <Inbox className="w-10 h-10 mx-auto text-sg-mute" />
            <p className="font-display text-2xl mt-3 text-sg-navy">No receipts yet.</p>
            <p className="text-sm text-sg-mute mt-1">
              Share your branded portal link — every WhatsApp tap will show up here in real-time.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-sg-line">
            {data.receipts.map((r) => (
              <div
                key={r.receipt_id}
                className={`px-6 py-5 grid grid-cols-1 md:grid-cols-[80px_1fr_auto] gap-4 items-center ${
                  r.seen ? "" : "bg-sg-cream-soft/50"
                }`}
                data-testid={`receipt-${r.receipt_id}`}
              >
                <div className="hidden md:block">
                  {r.thumbnail_url ? (
                    <img src={r.thumbnail_url} alt={r.project_name || ""} className="w-16 h-16 rounded-xl object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-sg-cream-soft border border-sg-line flex items-center justify-center">
                      {r.event_type === "wa_click" ? <MessageCircle className="w-5 h-5 text-sg-gold-deep" /> : <Phone className="w-5 h-5 text-sg-gold-deep" />}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!r.seen && <span className="w-2 h-2 rounded-full bg-sg-gold" data-testid={`unseen-dot-${r.receipt_id}`} />}
                    <Badge className={`rounded-full ${r.event_type === "wa_click" ? "bg-sg-navy text-white" : "bg-sg-cream-soft text-sg-navy"}`}>
                      {r.event_type === "wa_click" ? "WhatsApp Tap" : "Call Tap"}
                    </Badge>
                    {r.source && <span className="overline text-sg-mute">via {r.source.replace("_", " ")}</span>}
                    {r.webhook_status && (
                      <Badge variant="outline" className={`rounded-full text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLE[r.webhook_status] || "border-sg-line text-sg-mute"}`}>
                        webhook · {r.webhook_status}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-2 text-sg-navy">
                    A visitor tapped on{" "}
                    {r.project_id && r.project_id !== "mp_test" ? (
                      <Link to={`/project/${r.project_id}`} className="font-medium text-sg-navy underline decoration-sg-gold/60 underline-offset-2">
                        {r.project_name || r.project_id}
                      </Link>
                    ) : (
                      <span className="font-medium">{r.project_name || "your branded portal"}</span>
                    )}
                    {r.locality_tag && <span className="text-sg-mute"> · {r.locality_tag}</span>}
                  </p>
                  <p className="text-xs text-sg-mute mt-1 font-mono">
                    {timeAgo(r.created_at)} · {r.device_summary || "Unknown device"} · {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 md:flex-col md:items-end">
                  {!r.seen && (
                    <Button size="sm" variant="outline" className="rounded-full border-sg-line" onClick={() => markSeen(r.receipt_id)} data-testid={`mark-seen-${r.receipt_id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Mark seen
                    </Button>
                  )}
                  {r.project_id && r.project_id !== "mp_test" && (
                    <Link
                      to={`/project/${r.project_id}`}
                      className="text-xs text-sg-gold-deep inline-flex items-center gap-1 hover:underline"
                      data-testid={`receipt-open-${r.receipt_id}`}
                    >
                      Open project <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
